<script setup lang="ts">
import type { Product } from '~/models/Product'
import type { ColorPair } from '~/models/Presentation/BasePage'
import type { ContentResource } from '~/models/Content/ContentResource'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useContentHelper } from '~/composables/useContentHelper'
import useColors from '~/composables/colors'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = withDefaults(defineProps<{
  resource: BaseItem | ContentPackage | ContentResource
  isAnchor?: boolean
  hasAccess?: boolean
  product?: Product
  colorPair?: ColorPair
  loading?: 'eager' | 'lazy'
}>(), {
  isAnchor: false,
  hasAccess: true,
  product: undefined,
  loading: 'lazy'
})

const { getTitle } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { t } = useI18n()

const elementRef = ref()

const { backgroundStyle } = useColors({
  identifier: props.resource.locationId!,
  elementRef,
})

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))
const imageIsFallback = computed(() => (props.resource as ContentResource)?.imageIsFallback ?? false)
</script>

<template>
  <article
    class="motion-safe:animate-fade-in-down relative h-full min-h-[200px] min-w-[215px] rounded-2xl text-lg"
    :class="{ 'h-[230px] w-[250px]': colorPair }"
  >
    <div
      ref="elementRef"
      class="relative h-full max-w-full overflow-hidden rounded-2xl"
      :class="colorPair ? 'aspect-auto' : 'aspect-square'"
    >
      <aside
        v-if="resource.new"
        class="bg-green-40 absolute top-0 right-0 z-20 rounded-tr-2xl rounded-bl-2xl p-2 text-sm font-medium tracking-wider text-white uppercase"
        v-text="t('new')"
      />
      <img
        v-if="resource.thumbnail"
        :src="resource.thumbnail"
        class="block size-full rounded-xl object-cover"
        :class="{ 'opacity-0': imageIsFallback }"
        :loading
        :alt="resource.thumbnailAlt"
        aria-hidden="true"
      >
      <div
        v-else
        class="absolute inset-0"
        :style="backgroundStyle"
      />
    </div>
    <div class="absolute bottom-0 z-10 flex size-full flex-col items-center justify-end rounded-2xl bg-gray-50/10 text-center text-white">
      <div class="h-max max-w-[80%] min-w-[50%] rounded-t-xl bg-white px-4 py-3">
        <span class="line-clamp-3 overflow-hidden text-base font-semibold hyphens-auto text-blue-50 sm:text-lg">
          <LinkOrAnchor
            :link="link"
            class="outline-0 before:absolute before:inset-0 before:z-10 before:rounded-2xl before:shadow-md before:transition-shadow before:hover:shadow-lg before:focus-visible:shadow-lg before:focus-visible:ring"
            :is-anchor="props.isAnchor"
          >
            {{ title }}
          </LinkOrAnchor>
        </span>
      </div>
    </div>
    <CardMenu
      :resource="resource"
      button-classes="absolute left-4 top-4 z-10"
    />
  </article>
</template>
